import React from 'react';
import { useIsDisabledByPermission } from '../contexts/PermissionAccessContext';

export type ToggleVariant = 'simple' | 'centered' | 'with-icons' | 'with-label';
export type ToggleSize = 'xs' | 'sm' | 'md' | 'lg';

export interface ToggleProps {
  checked?: boolean;
  onChange?: (checked: boolean) => void;
  variant?: ToggleVariant;
  size?: ToggleSize;
  name?: string;
  ariaLabel?: string;
  label?: string;
  description?: string;
  disabled?: boolean;
  className?: string;
}

export default function Toggle({
  checked = false,
  onChange,
  variant = 'simple',
  size = 'md',
  name = 'setting',
  ariaLabel = 'Toggle setting',
  label,
  description,
  disabled: disabledProp = false,
  className = ''
}: ToggleProps) {
  const disabledByPermission = useIsDisabledByPermission();
  const disabled = disabledProp || disabledByPermission;

  const handleChange = (e: React.ChangeEvent<HTMLInputElement>) => {
    if (!disabled && onChange) {
      onChange(e.target.checked);
    }
  };

  const getSizeClasses = () => {
    switch (size) {
      case 'xs':
        return {
          container: 'w-7 h-4',
          knob: 'size-3',
          translate: 'translate-x-3'
        };
      case 'sm':
        return {
          container: 'w-9',
          knob: 'size-4',
          translate: 'translate-x-4'
        };
      case 'md':
        return {
          container: 'w-11',
          knob: 'size-5',
          translate: 'translate-x-5'
        };
      case 'lg':
        return {
          container: 'w-14',
          knob: 'size-6',
          translate: 'translate-x-7'
        };
      default:
        return {
          container: 'w-11',
          knob: 'size-5',
          translate: 'translate-x-5'
        };
    }
  };

  const sizeClasses = getSizeClasses();

  // Simple variant
  if (variant === 'simple') {
    return (
      <div className={`group relative inline-flex ${sizeClasses.container} shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out ${checked ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
        <span className={`${sizeClasses.knob} rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${checked ? sizeClasses.translate : ''}`}></span>
        <input
          type="checkbox"
          name={name}
          aria-label={ariaLabel}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 appearance-none focus:outline-hidden cursor-pointer disabled:cursor-not-allowed"
        />
      </div>
    );
  }

  // Centered variant
  if (variant === 'centered') {
    const centeredSizes = size === 'sm' ? 'h-4 w-8' : size === 'lg' ? 'h-6 w-12' : 'h-5 w-10';
    const bgSizes = size === 'sm' ? 'h-3 w-7' : size === 'lg' ? 'h-5 w-11' : 'h-4 w-9';

    return (
      <div className={`group relative inline-flex ${centeredSizes} shrink-0 items-center justify-center rounded-full outline-offset-2 outline-indigo-600 ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
        <span className={`absolute mx-auto ${bgSizes} rounded-full inset-ring inset-ring-gray-900/5 transition-colors duration-200 ease-in-out ${checked ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'}`}></span>
        <span className={`absolute left-0 ${sizeClasses.knob} rounded-full border border-gray-300 bg-white shadow-xs transition-transform duration-200 ease-in-out ${checked ? sizeClasses.translate : ''}`}></span>
        <input
          type="checkbox"
          name={name}
          aria-label={ariaLabel}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 appearance-none focus:outline-hidden cursor-pointer disabled:cursor-not-allowed"
        />
      </div>
    );
  }

  // With icons variant
  if (variant === 'with-icons') {
    return (
      <div className={`group relative inline-flex ${sizeClasses.container} shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out ${checked ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
        <span className={`relative ${sizeClasses.knob} rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${checked ? sizeClasses.translate : ''}`}>
          {/* X icon (unchecked) */}
          <span aria-hidden="true" className={`absolute inset-0 flex size-full items-center justify-center transition-opacity duration-200 ease-in ${checked ? 'opacity-0 duration-100 ease-out' : 'opacity-100'}`}>
            <svg viewBox="0 0 12 12" fill="none" className="size-3 text-gray-400">
              <path d="M4 8l2-2m0 0l2-2M6 6L4 4m2 2l2 2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
            </svg>
          </span>
          {/* Check icon (checked) */}
          <span aria-hidden="true" className={`absolute inset-0 flex size-full items-center justify-center transition-opacity duration-100 ease-out ${checked ? 'opacity-100 duration-200 ease-in' : 'opacity-0'}`}>
            <svg viewBox="0 0 12 12" fill="currentColor" className="size-3 text-indigo-600">
              <path d="M3.707 5.293a1 1 0 00-1.414 1.414l1.414-1.414zM5 8l-.707.707a1 1 0 001.414 0L5 8zm4.707-3.293a1 1 0 00-1.414-1.414l1.414 1.414zm-7.414 2l2 2 1.414-1.414-2-2-1.414 1.414zm3.414 2l4-4-1.414-1.414-4 4 1.414 1.414z" />
            </svg>
          </span>
        </span>
        <input
          type="checkbox"
          name={name}
          aria-label={ariaLabel}
          checked={checked}
          onChange={handleChange}
          disabled={disabled}
          className="absolute inset-0 appearance-none focus:outline-hidden cursor-pointer disabled:cursor-not-allowed"
        />
      </div>
    );
  }

  // With label variant
  if (variant === 'with-label' && label) {
    const labelId = `${name}-label`;
    const descriptionId = description ? `${name}-description` : undefined;

    return (
      <div className={`flex items-center justify-between gap-3 ${className}`}>
        <div className={`group relative inline-flex ${sizeClasses.container} shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out ${checked ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''}`}>
          <span className={`${sizeClasses.knob} rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${checked ? sizeClasses.translate : ''}`}></span>
          <input
            id={name}
            type="checkbox"
            name={name}
            aria-labelledby={labelId}
            aria-describedby={descriptionId}
            checked={checked}
            onChange={handleChange}
            disabled={disabled}
            className="absolute inset-0 appearance-none focus:outline-hidden cursor-pointer disabled:cursor-not-allowed"
          />
        </div>

        <div className="text-sm">
          <label id={labelId} htmlFor={name} className={`font-medium text-gray-900 dark:text-white ${disabled ? 'cursor-not-allowed' : 'cursor-pointer'}`}>
            {label}
          </label>
          {description && (
            <span id={descriptionId} className="text-muted"> {description}</span>
          )}
        </div>
      </div>
    );
  }

  // Default fallback to simple
  return (
    <div className={`group relative inline-flex ${sizeClasses.container} shrink-0 rounded-full p-0.5 inset-ring inset-ring-gray-900/5 outline-offset-2 outline-indigo-600 transition-colors duration-200 ease-in-out ${checked ? 'bg-indigo-600' : 'bg-gray-200 dark:bg-gray-600'} ${disabled ? 'opacity-50 cursor-not-allowed' : ''} ${className}`}>
      <span className={`${sizeClasses.knob} rounded-full bg-white shadow-xs ring-1 ring-gray-900/5 transition-transform duration-200 ease-in-out ${checked ? sizeClasses.translate : ''}`}></span>
      <input
        type="checkbox"
        name={name}
        aria-label={ariaLabel}
        checked={checked}
        onChange={handleChange}
        disabled={disabled}
        className="absolute inset-0 appearance-none focus:outline-hidden cursor-pointer disabled:cursor-not-allowed"
      />
    </div>
  );
}
